import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';
import { loadConversions, buildFamilies, keyOf } from './conversions';
import { newestReports, stockAt, austinStock } from './morningReports';

/*
  WHAT A RUN NEEDS, AND SAVING IT. Ordering stage 4, 12 October 2026.

  Everything `planRun` (runPlan.js) works from, read for one job: the
  conversions, the destination's Stock Status rows for every number in every
  family on the job, our items for every number involved (vendor, cost each,
  Account Edge number), Austin's spare, the vendor and item rules, live back
  orders, and how often each item has been skipped for this office before.
*/

async function itemsFor(keys) {
  const out = new Map();
  const want = [...new Set(keys.filter(Boolean))];
  for (let i = 0; i < want.length; i += 150) {
    const { data, error } = await supabase.from('org_item_numbers')
      .select('number_key, org_item_id').eq('system', 'claimsoft').in('number_key', want.slice(i, i + 150));
    if (error) return { items: out, error };
    const ids = [...new Set((data || []).map(r => r.org_item_id))];
    if (!ids.length) continue;
    const { data: oc, error: e2 } = await supabase.from('org_catalog')
      .select('org_item_id, catalog_item_id, vendor, account_edge_number, cost_each').in('org_item_id', ids);
    if (e2) return { items: out, error: e2 };
    const cats = [...new Set((oc || []).map(r => r.catalog_item_id))];
    const { data: ci } = await supabase.from('catalog_items').select('id, vendor_id').in('id', cats);
    const vend = new Map((ci || []).map(c => [c.id, c.vendor_id]));
    const byOrg = new Map((oc || []).map(r => [r.org_item_id, r]));
    for (const r of data || []) {
      const o = byOrg.get(r.org_item_id);
      if (o) out.set(r.number_key, { catalog_item_id: o.catalog_item_id, vendor_id: vend.get(o.catalog_item_id) || null,
        vendor: o.vendor, ae_number: o.account_edge_number, cost: o.cost_each });
    }
  }
  return { items: out, error: null };
}

/* How many earlier processed runs for this office skipped each item. Counted
   since the item was last ordered or moved there, because a run that got it
   resets the count. */
async function skipsAt(destinationId, jobId) {
  const out = new Map();
  if (!destinationId) return out;
  const { data: jobs } = await supabase.from('order_jobs').select('id, processed_at')
    .eq('destination_id', destinationId).not('processed_at', 'is', null).neq('id', jobId).order('processed_at');
  const ids = (jobs || []).map(j => j.id);
  if (!ids.length) return out;
  const when = new Map((jobs || []).map(j => [j.id, j.processed_at]));
  const { data: lines } = await fetchAll(() => supabase.from('order_job_lines')
    .select('id, job_id, final_item_number, route').in('job_id', ids).in('route', ['skip', 'order', 'transfer']).order('id'));
  for (const l of [...(lines || [])].sort((a, b) => String(when.get(a.job_id)).localeCompare(String(when.get(b.job_id))))) {
    const k = keyOf(l.final_item_number);
    if (!k) continue;
    out.set(k, l.route === 'skip' ? (out.get(k) || 0) + 1 : 0);
  }
  return out;
}

export async function loadRunInputs(job, lines) {
  const conv = await loadConversions();
  const families = conv.error ? null : buildFamilies(conv.rows);
  const keys = new Set();
  for (const l of lines) {
    const k = keyOf(l.item_as_given);
    keys.add(k);
    if (families && families.familyOf.has(k)) families.members.get(families.familyOf.get(k)).forEach(m => keys.add(m));
  }
  const { reports, error: rErr } = await newestReports();
  if (rErr) return { error: rErr };
  const ss = reports.stock_status, ec = reports.excess_calc;
  const dest = job.destination ? job.destination.name : null;
  const [st, it] = await Promise.all([
    ss && dest ? stockAt(ss.id, dest, [...keys]) : Promise.resolve({ stock: null }),
    itemsFor([...keys]),
  ]);
  if (st.error) return { error: st.error };
  if (it.error) return { error: it.error };
  const aes = [...it.items.values()].map(i => i.ae_number).filter(Boolean);
  const au = ec ? await austinStock(ec.id, aes) : { austin: null };
  if (au.error) return { error: au.error };
  const [vr, ir, bo] = await Promise.all([
    supabase.from('vendor_order_rules').select('vendor_id, min_order_value, special_mode'),
    supabase.from('item_order_rules').select('item_or_prefix, is_prefix, vendor_id, routing_override, min_quantity, order_multiple'),
    supabase.from('back_orders').select('vendor_id, item_number, use_instead, expected_until').is('ended_at', null),
  ]);
  const err = vr.error || ir.error || bo.error;
  if (err) return { error: err };
  return {
    error: null, reports, families,
    stock: st.stock, items: it.items, austin: au.austin,
    vendorRules: new Map((vr.data || []).map(r => [r.vendor_id, r])),
    itemRules: ir.data || [], backOrders: bo.data || [],
    priorSkips: await skipsAt(job.destination_id, job.id),
  };
}

/* What he set by hand on an earlier save, so reopening a run keeps it. */
export function overridesFrom(lines) {
  const m = new Map();
  for (const l of lines) {
    if (!l.changed_by_hand) continue;
    m.set(l.id, { item: l.final_item_number, qty: l.final_quantity, route: l.route });
  }
  return m;
}

/*
  **Saving the run.** Every line gets its route, why, the item and quantity to
  use, and the arithmetic; the job gets when, by whom and which reports. The
  job stays Being worked: the Account Edge file is the next stage. Proved by
  reading the rows back.
*/
export async function saveRun({ job, plan, reports, userId }) {
  const now = new Date().toISOString();
  for (const p of plan.lines) {
    const { data, error } = await supabase.from('order_job_lines').update({
      route: p.route, route_reason: p.reason.join('. '), final_item_number: p.item, final_catalog_item_id: p.catalog_item_id,
      final_quantity: p.qty, audit: p.audit, changed_by_hand: !!p.changedByHand, decided_by: userId || null, decided_at: now, updated_at: now,
    }).eq('id', p.id).eq('job_id', job.id).select('id').maybeSingle();
    if (error) return { ok: false, error };
    if (!data) return { ok: false, error: { message: 'Line ' + p.line_no + ' did not save. Open the job again and check it.' } };
  }
  const { data: j, error } = await supabase.from('order_jobs').update({
    processed_at: now, processed_by: userId || null, updated_at: now,
    stock_report_id: reports && reports.stock_status ? reports.stock_status.id : null,
    excess_report_id: reports && reports.excess_calc ? reports.excess_calc.id : null,
  }).eq('id', job.id).eq('state', 'working').select('id').maybeSingle();
  if (error) return { ok: false, error };
  if (!j) return { ok: false, error: { message: 'The job is no longer being worked, so the run was not saved.' } };
  return { ok: true };
}
